import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { ICountry, ICountryInput } from "models/country";
import React, { useState } from "react";
import { useMutation } from "react-query";

import {
  CountryCells,
  addCountry,
  deleteCountry,
  editCountry,
  getCountries,
} from "framework/countries";
import AddCountry from "./partials/AddCountry/AddCountry";
import BlockIcon from "@mui/icons-material/Block";
import EditCountry from "./partials/EditCountry/EditCountry";
import DeleteCountry from "./partials/DeleteCountry/DeleteCountry";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";

interface Props {
  feature: Feature;
}

const Countries: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const user = getUserData();
  const { colors } = user;
  const [loading2, setLoading2] = useState<boolean>(false);

  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<ICountry>(
    {} as ICountry
  );
  const [FilterCountries, setFilterCountries] = useState<ICountry[]>(
    [] as ICountry[]
  );
  const [inputObj, setInputObj] = useState<ICountryInput>({} as ICountryInput);
  const { setAlert } = useAlert();

  const addCountriesMutation = useMutation({
    mutationFn: (createInput: ICountryInput) => {
      return addCountry(createInput);
    },
  });

  const editCountriesMutation = useMutation({
    mutationFn: (data: { createInput: ICountryInput; id: string }) => {
      return editCountry(data.createInput, data.id);
    },
  });

  const deleteCountriesMutation = useMutation({
    mutationFn: (data: { code: string; id: string }) => {
      return deleteCountry(data.code, data.id);
    },
  });

  const {
    loading,
    data: Countries,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCountries(),
    fetchPermission: () => getPermission(2),
    section: "Countries",
    onSuccess: (data) => setFilterCountries(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (error) return <div>{error}</div>;

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Country: ICountry) => {
    setSelectedCountry(Country);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCountry({} as ICountry);
  };

  const handleOpenDelete = (Country: ICountry) => {
    setSelectedCountry(Country);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCountry({} as ICountry);
  };

  const getBodyCells = (row: ICountry) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "code")
        return {
          id: "code",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.code}</Typography>
            </TableCell>
          ),
        };
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"right"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.code || !inputObj.name || !inputObj.nameAr)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addCountriesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Country added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as ICountryInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: ICountryInput = {
        name: inputObj.name || selectedCountry.name,
        code: selectedCountry.code,
        nameAr: inputObj.nameAr || selectedCountry.nameAr,
      };
      await editCountriesMutation.mutateAsync({
        createInput,
        id: selectedCountry.id,
      });
      setAlert({
        open: true,
        message: "Country updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as ICountryInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCountriesMutation.mutateAsync({
        code: selectedCountry.code,
        id: selectedCountry.id,
      });
      setAlert({
        open: true,
        message: "Country deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCountries(Countries!);
    let filtered = Countries!.filter((city) =>
      city.name.toLowerCase().includes(value.toLowerCase()) || city.nameAr?.toLowerCase()?.includes(value.toLowerCase()) || city.code?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterCountries(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_country")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterCountries.length > 0 && (
          <List
            headCells={CountryCells}
            data={FilterCountries}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCountries || FilterCountries.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_country_header")}
              message={t("T_no_country_message")}
            />
          )}
        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCountry
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCountry
          open={openEdit}
          loading={loading2}
          country={selectedCountry}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCountry
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Countries;
